import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import imageJacob from "@/assets/testimonial-jacob-new.png";
import imageWisera from "@/assets/wisera-logo.jpg";
import imageHorsMarche from "@/assets/hors-marche-logo.jpg";




/**
 * Réalisations — galerie éditoriale asymétrique.
 * Les médias sont en stand-by : chaque projet utilise un slot placeholder nommé,
 * à remplacer par le fichier fourni (video ou image).
 */
type Project = {
  slot: "FEATURED_PROJECT" | "PROJECT_02" | "PROJECT_03";
  client: string;
  objective: string;
  format?: string;
  href: string;
  ratio: string;
  video?: string;
  image?: string;
  focal?: string;
};

const projects: Record<Project["slot"], Project> = {
  FEATURED_PROJECT: {
    slot: "FEATURED_PROJECT",
    client: "Hors Marché Québec",
    objective: "Campagne + conversion",
    format: "Publicité",
    href: "/etudes-de-cas",
    ratio: "aspect-[16/10]",
    image: imageHorsMarche,
    focal: "center center",
  },
  PROJECT_02: {
    slot: "PROJECT_02",
    client: "Wisera",
    objective: "Contenu + génération de demande",
    format: "Contenu",
    href: "/etudes-de-cas",
    ratio: "aspect-[5/4]",
    image: imageWisera,
    focal: "center center",
  },
  PROJECT_03: {
    slot: "PROJECT_03",
    client: "Jacob Saey-Williamson",
    objective: "Personal brand + acquisition",
    format: "Vidéo",
    href: "/etudes-de-cas",
    ratio: "aspect-[16/9]",
    image: imageJacob,
    focal: "center 25%",
  },

};


const ProjectCard = ({
  project,
  large = false,
  fill = false,
}: {
  project: Project;
  large?: boolean;
  fill?: boolean;
}) => (
  <Link
    to={project.href}
    onClick={() => trackEvent("portfolio_viewed", { project: project.client })}
    className={`group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-2xl ${
      fill ? "flex h-full min-h-0 flex-col" : "block"
    }`}
  >
    <div
      className={`relative overflow-hidden rounded-2xl bg-secondary ${
        fill ? `min-h-0 flex-1 ${project.ratio} md:aspect-auto` : project.ratio
      }`}
    >

      {project.video ? (
        <video
          src={project.video}
          muted
          loop
          playsInline
          preload="none"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
        />
      ) : project.image ? (
        <img
          src={project.image}
          alt={`${project.client} — ${project.objective}`}
          loading="lazy"
          decoding="async"
          style={{ objectPosition: project.focal ?? "center 30%" }}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center transition-transform duration-700 ease-out group-hover:scale-[1.02]">
          <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground/70 md:text-xs">
            {project.slot}
          </span>
        </div>
      )}

      {/* Voile très léger, uniquement au hover */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/70 via-background/10 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100"
      />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-2 p-5 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100 md:p-6">
        <span className="inline-flex items-center gap-1.5 text-sm text-foreground">
          Voir le projet
          <ArrowUpRight className="h-4 w-4" />
        </span>
      </div>
    </div>

    <div className="mt-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
      <div>
        <h3 className={`font-semibold tracking-tight text-foreground ${large ? "text-xl md:text-2xl" : "text-base md:text-lg"}`}>
          {project.client}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">{project.objective}</p>
      </div>
      {project.format && (
        <span className="shrink-0 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/60">
          {project.format}
        </span>
      )}
    </div>
  </Link>
);

const CreativeV2 = () => (
  <section id="realisations" aria-labelledby="creation-titre" className="scroll-mt-24 py-14 md:py-24">
    <div className="mx-auto max-w-[1280px] px-5 md:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6 }}
        className="max-w-3xl"
      >
        <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-primary">Réalisations</p>
        <h2
          id="creation-titre"
          className="mt-4 max-w-[22ch] text-[1.7rem] font-semibold leading-[1.12] tracking-tight text-foreground md:mt-5 md:max-w-none md:text-5xl"
        >
          Le contenu capte l'attention. Le système transforme la suite.
        </h2>
        <p className="mt-4 max-w-[38ch] text-[0.95rem] leading-[1.55] text-muted-foreground md:mt-5 md:max-w-none md:text-lg">
          Chaque projet commence par une idée créative, mais s'inscrit dans un objectif de croissance plus large.
        </p>
      </motion.div>

      {/* Rangée 1 : grande réalisation (≈62%) + deux projets plus petits */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.6 }}
        className="mt-8 grid gap-6 md:mt-14 md:grid-cols-12 md:items-stretch"
      >
        <div className="md:col-span-7 lg:col-span-8">
          <ProjectCard project={projects.FEATURED_PROJECT} large />
        </div>
        <div className="grid gap-6 md:col-span-5 md:grid-rows-2 lg:col-span-4">
          <ProjectCard project={projects.PROJECT_02} fill />
          <ProjectCard project={projects.PROJECT_03} fill />
        </div>
      </motion.div>






      <div className="mt-8 md:mt-10">
        <Button
          asChild
          size="lg"
          variant="outline"
          className="w-full rounded-full border-border px-7 py-5 text-base font-medium text-foreground hover:bg-secondary sm:w-auto md:py-6"
        >
          <Link to="/etudes-de-cas" onClick={() => trackEvent("portfolio_viewed", { location: "section_creation" })}>
            Explorer nos études de cas
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  </section>
);

export default CreativeV2;
